import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { sortBy } from 'lodash';

import SessionStore from 'stores/SessionStore';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import { Modal, ModalBody } from 'components/ui/modal';
import DataPoller from 'components/ui/DataPoller';
import {
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHeader,
  SortableColumnHeader,
} from 'components/ui/table';

import DeclineButton from 'components/v2/buttons/DeclineButton';
import ApproveButton from 'components/v2/buttons/ApproveButton';

const AccountAccessRequestModal = ({
  showAccountAccessModal,
  handleSingleRequest,
  accessRequest,
  onAccountActionClose,
}) => {
  const { t } = useTranslation();
  const [requestAccountAccessModal, setRequestAccountAccessModal] = useState(
    showAccountAccessModal || false,
  );
  const [pendingAccountAccessList, setPendingAccountAccessList] = useState(
    accessRequest ? [accessRequest] : [],
  );

  const accountAccessRequestCount = async () => {
    try {
      if (!handleSingleRequest) {
        const grantorId = SessionStore.getUser()._id;
        const result = await ShipmentWebAPIUtils.getAccountAccessRequestCount(
          grantorId,
          (e) => {
            throw new Error(`Account Access Request returned ${e}`);
          },
        );

        if (result && result.entity && result.entity.success) {
          const pendingList = result.entity.data || [];
          if (pendingList.length) {
            setPendingAccountAccessList(sortBy(pendingList, ['created_at']));
          }
          const requestsRead =
            SessionStore.getAccessAccountRequest(grantorId) || [];
          if (!requestsRead?.length) {
            setRequestAccountAccessModal(true);
          }
        }
      } else {
        setRequestAccountAccessModal(true);
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    accountAccessRequestCount();
  }, []);

  function renderAccessAccountRequestModal() {
    return (
      <Modal
        title={t('PARTNER_ACCESS_REQUEST')}
        isOpen={requestAccountAccessModal}
        onClose={onHide}
        panelClassName="sm:max-w-xl"
      >
        <ModalBody>
          {getBody(pendingAccountAccessList, handleActionClick)}
        </ModalBody>
      </Modal>
    );
  }

  function onHide() {
    onClickCancel(pendingAccountAccessList);
    setRequestAccountAccessModal(false);
  }

  function getBody(grants, handleActionClick) {
    return (
      <>
        <div className="text-sm font-semibold py-2" data-testid="partner-access-modal-note">
          {t('PARTNER_ACCESS_REQUEST_NOTE')}
        </div>
        <div
          className="table-wrapper overflow-auto relative"
          style={{ maxHeight: 'calc(100dvh - 230px)' }}
          data-testid="partner-access-modal-table"
        >
          <Table className="w-full">
            <TableHeader className="sticky top-0 bg-gray-50">
              <TableRow>
                <SortableColumnHeader className="w-64">
                  {t('NAME')}
                </SortableColumnHeader>
                <SortableColumnHeader className="w-64">
                  {t('EMAIL')}
                </SortableColumnHeader>
                <SortableColumnHeader className="w-32">
                  {t('ACTIONS')}
                </SortableColumnHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              {grants?.map((request) => (
                <TableRow key={request._id}>
                  <TableCell className="w-64">
                    {request?.grantee?.name}
                  </TableCell>
                  <TableCell className="w-64">
                    {request?.grantee?.email}
                  </TableCell>
                  <TableCell className="w-32 flex justify-start items-center gap-2">
                    <ApproveButton
                      onClick={() => handleActionClick(request, 'approved')}
                    />
                    <DeclineButton
                      onClick={() => handleActionClick(request, 'declined')}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </>
    );
  }

  const setModalData = (requests, showRequestModal) => {
    if (!requests.length) {
      setRequestAccountAccessModal(showRequestModal);
    }
  };

  const onActionClick = (grants = []) => {
    grants.forEach((grant) => {
      setPendingAccountAccessList((currentRequests) => {
        const pendingAccountAccessList = currentRequests.filter(
          (request) => request._id !== grant._id,
        );
        setModalData(pendingAccountAccessList, false);
        return pendingAccountAccessList;
      });
    });
    if (onAccountActionClose) {
      onAccountActionClose();
    }
  };

  const handleApprove = async (
    requestId,
    grantorId,
    granteeUserAccountId,
    grantorEmail,
  ) => {
    const result = await ShipmentWebAPIUtils.setAccountAccessRequestApprove(
      requestId,
      grantorId,
      granteeUserAccountId,
      grantorEmail,
      { role: 'admin', _type: 'admin' }, // we'll need to update this if we ever do custom role assignment
    ).catch((e) => {
      throw e;
    });
    return result;
  };

  const handleDecline = async (
    requestId,
    grantorId,
    granteeUserAccountId,
    grantorEmail,
  ) => {
    const result = await ShipmentWebAPIUtils.setAccountAccessRequestDecline(
      requestId,
      grantorId,
      granteeUserAccountId,
      grantorEmail,
      { role: 'admin', _type: 'admin' },
    );
    return result;
  };

  const handleActionClick = async (grant, clickOption) => {
    const granteeUserAccountId = grant.grantee._id;
    const grantorEmail = grant.grantor_email;
    const grantorId = grant.grantor._id;
    try {
      const res =
        clickOption === 'approved'
          ? await handleApprove(
              grant._id,
              grantorId,
              granteeUserAccountId,
              grantorEmail,
            )
          : await handleDecline(
              grant._id,
              grantorId,
              granteeUserAccountId,
              grantorEmail,
            );

      if (res.entity.success) {
        onActionClick([grant]);
      }
    } catch (e) {
      console.error(
        `Error while ${clickOption} the Account access request:`,
        e,
      );
    }
  };

  const onClickCancel = (grants = []) => {
    if (grants && grants?.length) {
      SessionStore.storeAccessAccountRequest(grants[0].grantor._id, grants);
      onActionClick(grants);
    }
  };

  return (
    <>
      <DataPoller frequency={2000} action={() => accountAccessRequestCount()} />
      {pendingAccountAccessList &&
        pendingAccountAccessList.length > 0 &&
        renderAccessAccountRequestModal()}
    </>
  );
};

export default AccountAccessRequestModal;
